import {
  EntityTable,
  type EntityTableColumnDefinition,
} from "@repo/ui/tables/entityTable";
import { type FC, useMemo } from "react";

import type {
  TrainingParticipant,
  TrainingParticipantChapter,
} from "@repo/app-types/entities/trainingParticipant";
import { AssessmentCellChapterScore } from "./assessmentCellChapterScore.js";
import { ParticipantCell } from "./participantCell.js";

export interface ParticipantsTableProps {
  participants: readonly TrainingParticipant[];
  isLoadingParticipants?: boolean;
  isErrorParticipants?: boolean;
  onLoadMore?: () => void;
  onParticipantAction: (
    participant: TrainingParticipant,
    chapter?: TrainingParticipantChapter,
  ) => void;
}

export const ParticipantsTable: FC<ParticipantsTableProps> = (props) => {
  const { onParticipantAction } = props;
  const chapters = props.participants[0]?.course.chapters;

  const columns = useMemo<
    readonly EntityTableColumnDefinition<TrainingParticipant>[]
  >(() => {
    const participantColumn: EntityTableColumnDefinition<TrainingParticipant> =
      {
        id: "participant",
        header: "Participant",
        isRowHeader: true,
        renderCell: (participant) => (
          <ParticipantCell participant={participant} />
        ),
      };

    const chapterColumns = (chapters ?? []).map(
      (
        chapter,
        chapterIndex,
      ): EntityTableColumnDefinition<TrainingParticipant> => ({
        id: `chapter-${chapter.id}`,
        header: `Chapter ${chapterIndex + 1}`,
        // Set a fixed width so excess width will go to the most left-hand column
        className: "w-28",
        renderCell: (participant) => {
          const rowChapter = participant.course.chapters[chapterIndex];
          if (!rowChapter) {
            return null;
          }
          return (
            <AssessmentCellChapterScore
              chapterIndex={chapterIndex}
              chapter={rowChapter}
              assessment={participant.assessment?.chapters[rowChapter.id]}
              onClick={() => onParticipantAction(participant, rowChapter)}
            />
          );
        },
      }),
    );

    return [participantColumn, ...chapterColumns];
  }, [chapters, onParticipantAction]);

  return (
    <EntityTable
      aria-label="Training participants"
      columns={columns}
      entities={props.participants}
      isLoading={props.isLoadingParticipants}
      isError={props.isErrorParticipants}
      onLoadMore={props.onLoadMore}
      emptyMessage="No participants yet."
      onEntityAction={props.onParticipantAction}
    />
  );
};
